import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function Metric({ label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#111] p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#555]">{label}</p>
      <p className={`font-mono-data text-3xl font-semibold mt-3 ${accent || "text-[#111]"}`}>{value}</p>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    api.get("/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/summary/monthly?months=3").then((r) => setMonthly(r.data.months || [])).catch(() => {});
  }, []);

  const monthLabel = (m) => new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  const isAgent = user?.role === "agent";

  const statusData = s ? Object.entries(s.by_status).map(([name, value]) => ({ name, value })) : [];
  const countryData = s ? Object.entries(s.by_country).map(([name, value]) => ({ name, value })) : [];
  const colors = ["#0047FF", "#00C853", "#FFAB00", "#111111", "#8b5cf6", "#e11d48"];

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-xs tracking-widest text-[#0047FF]">DASHBOARD</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter">Overview</h2>
          <p className="text-sm text-[#555] mt-1">Real-time snapshot of {user?.role === "admin" ? "the whole business" : "your work"}.</p>
        </div>
      </div>

      {!s ? (
        <p className="font-mono text-sm text-[#555]">Loading metrics…</p>
      ) : (
        <>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${user?.role === "agent" ? "lg:grid-cols-5" : "lg:grid-cols-6"} gap-px bg-[#111] border border-[#111] mb-8`}>
            <Metric label="Total Leads" value={s.total_leads} testid="metric-total-leads" />
            <Metric label="Total Booking" value={inr(s.total_booking)} testid="metric-total-booking" />
            <Metric label="Collected" value={inr(s.total_received)} accent="text-[#00C853]" testid="metric-collected" />
            <Metric label="Pending" value={inr(s.pending_amount)} accent="text-[#FFAB00]" testid="metric-pending" />
            {user?.role !== "agent" && <Metric label="Paid to Writers" value={inr(s.total_writer_paid)} accent="text-[#0047FF]" testid="metric-writer-paid" />}
            <Metric label="Overdue" value={s.overdue_count} accent="text-[#e11d48]" testid="metric-overdue" />
          </div>

          {monthly.length > 0 && (
            <div className="bg-white border border-[#111] mb-8 overflow-x-auto" data-testid="monthly-breakdown">
              <div className="px-6 py-4 border-b border-[#111]">
                <h3 className="font-heading text-lg font-bold tracking-tight">Month-wise Breakdown</h3>
                <p className="text-xs text-[#555] font-mono mt-1">Last 3 months at a glance.</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#111] text-left font-mono text-[10px] uppercase tracking-wider text-[#555]">
                    <th className="px-6 py-3">Month</th>
                    <th className="px-6 py-3 text-right">Leads</th>
                    <th className="px-6 py-3 text-right">Booking</th>
                    <th className="px-6 py-3 text-right">Collected</th>
                    <th className="px-6 py-3 text-right">Pending</th>
                    {!isAgent && <th className="px-6 py-3 text-right">Paid to Writers</th>}
                    <th className="px-6 py-3 text-right">Overdue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#eee]">
                  {monthly.map((mo) => (
                    <tr key={mo.month} className="hover:bg-[#fafafa] transition-colors" data-testid={`month-row-${mo.month}`}>
                      <td className="px-6 py-3 font-medium whitespace-nowrap">{monthLabel(mo.month)}</td>
                      <td className="px-6 py-3 text-right font-mono-data">{mo.total_leads}</td>
                      <td className="px-6 py-3 text-right font-mono-data">{inr(mo.total_booking)}</td>
                      <td className="px-6 py-3 text-right font-mono-data text-[#00C853]">{inr(mo.total_received)}</td>
                      <td className="px-6 py-3 text-right font-mono-data text-[#FFAB00]">{inr(mo.pending_amount)}</td>
                      {!isAgent && <td className="px-6 py-3 text-right font-mono-data text-[#0047FF]">{inr(mo.total_writer_paid)}</td>}
                      <td className="px-6 py-3 text-right font-mono-data text-[#e11d48]">{mo.overdue_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#111] p-6">
              <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Leads by Status</h3>
              {statusData.length === 0 ? <p className="text-sm text-[#555] font-mono">No data yet.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={statusData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
                    <Bar dataKey="value">
                      {statusData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-white border border-[#111] p-6">
              <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Leads by Country</h3>
              {countryData.length === 0 ? <p className="text-sm text-[#555] font-mono">No data yet.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={countryData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
                    <Bar dataKey="value" fill="#0047FF" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
